import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import * as _ from 'lodash';

import { Subscription } from 'rxjs';

import { Option } from 'shared/form/select/option.interface';
import { DetailsControlState } from './details-control-state';
import { DetailsControlType } from './details-control-type';

import { SelectDropdownComponent } from 'shared/dropdown/select-dropdown/select-dropdown.component';
import { DateInputComponent } from 'shared/form/date-input/date-input.component';
import { debounceTime, distinctUntilChanged, takeWhile } from 'rxjs/operators';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';


@Component({
  moduleId: module.id,
  selector: 'details-control',
  templateUrl: 'details-control.component.html',
  styleUrls: ['details-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsControlComponent implements OnInit, OnDestroy, OnChanges {
  // control label
  @Input() label: string = '';
  // switch between edit/view mode
  @Input() editMode: boolean = false;
  // hides left border in view mode
  @Input() hideLeftBorder: boolean = false;
  // control type switcher
  @Input() controlType: DetailsControlType = DetailsControlType.Input;
  // control's form group
  @Input() formGroup: UntypedFormGroup;
  // select's form group in case of different select's FormGroup
  // when controlType is combined (input + select)
  @Input() selectFormGroup: UntypedFormGroup;
  // used if we want to use details control in view mode only
  // without form control creating
  @Input() staticValue: any;
  // control's name in the form group
  // may be different with fieldMap in case of looping control
  @Input() controlName: string;
  // select's name in the form group
  // when controlType is combined (input + select)
  @Input() selectControlName: string;
  // options list for select dropdown
  // used if controlType is Select or Combined
  @Input() options: Array<Option>;
  // custom placeholder
  @Input() placeholder: string;
  // disabling select date since passing value
  // used if controlType is 'date'
  @Input() disableSince: { year: number; month: number; day: number };
  // used if controlType is 'date'
  @Input() openSelectorTopOfInput: boolean;
  // max characters length used by char counter
  @Input() maxLength: number;
  // Makes input readonly
  @Input() readonly: boolean;
  // Masks control through @Input
  @Input() set masked(value: boolean) {
    if (value) {
      this.controlState = DetailsControlState.Masked;
    } else {
      this.controlState = DetailsControlState.Default;
    }
  }
  @Input() maskClass: string = 'transparent'; // 'transparent', 'disabled', 'no-changes'
  @Input() textMask: any;

  // Allows typing numbers only
  @Input() numerical: boolean = false;
  // Allows decimal point
  @Input() allowDecimal: boolean = false;

  // controls fieldMap, now used by default ghost value
  @Input() fieldMap: string;
  // when controlType is combined (input + select)
  @Input() selectFieldMap: string;
  @Input() selectedCombinedItem: any;
  // use this @Input if yuo need to rewrite common controls ghost
  @Input() controlGhostValue: string;
  // when controlType is combined (input + select)
  @Input() selectGhostValue: string;
  // Hides label via ngIf, so only value is visible
  @Input() hideLabel: boolean = false;
  // Hides label via opacity & visibility
  // thus all elemets save their places
  @Input() invisibleLabel: boolean = false;
  // Aligns content to the right
  @Input() alignRight: boolean = false;
  // Displays dots on the left indicating element as draggable
  @Input() draggable: boolean = false;
  // Shows any text after control or control value
  @Input() subText: string;
  // Marks subtext as invalid
  @Input() isSubTextInvalid: boolean;
  // Mark conrtrol as invalid
  @Input() forceInvalid: boolean = false;
  // Hide value of select dropdown component
  @Input() hideSelectDropdownValue: boolean = false;
  // Hide value of combined select dropdown component
  @Input() hideCombinedSelectDropdownValue: boolean = false;
  // Shows any html before control or control value
  @Input() prefixIcon: string;
  // Language icon for select-dropdown
  @Input() isLanguage: boolean = false;
  // Turn on decimalsAutocomplete directive
  // Used for prices autocomplete
  @Input() decimalsAutocomplete: boolean = false;
  @Input() decimalsLimit: number = 4;
  // if option from this array is selected then we show placeholder instead of it
  // used when controlType is select
  @Input() placeholderOptions: Array<any>;

  @Input() defaultDatePlaceholder: string = 'Select';
  @Input() defaultPlaceholder: string = 'Add';
  @Input() viewValuePlaceholder: string = '-';

  // typeahed inputs:
  // https://valor-software.com/ngx-bootstrap/#/typeahead#api-reference
  @Input() typeahead: any;
  @Input() typeaheadWaitMs: number = 500;
  @Input() typeaheadOptionsLimit: number = 50;
  @Input() typeaheadOptionField: string = 'name';

  // Make value with font regular weight
  @Input() isFontRegular: boolean = false;
  // static value for template type
  @Input() templateValue: string = '';

  @Output() dateChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectedChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() inputClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() controlFocused: EventEmitter<any> = new EventEmitter<any>();
  @Output() controlBlured: EventEmitter<any> = new EventEmitter<any>();
  @Output() controlStatusChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectStatusChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() controlPopulated: EventEmitter<number> = new EventEmitter<number>();
  @Output() selectPopulated: EventEmitter<number> = new EventEmitter<number>();
  @Output() controlValueChanged: EventEmitter<string|number> = new EventEmitter<string|number>();
  // typeahead outputs
  @Output() typeaheadLoading: EventEmitter<any> = new EventEmitter<any>();
  @Output() typeaheadNoResults: EventEmitter<any> = new EventEmitter<any>();
  @Output() typeaheadOnSelect: EventEmitter<any> = new EventEmitter<any>();
  // assets outputs
  @Output() assetClick: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild(SelectDropdownComponent, {static: false})
  selectDropdown: SelectDropdownComponent | void;

  @ViewChild('controlElement', {static: false})
  controlElement: ElementRef | void;

  @ViewChild(DateInputComponent, {static: false})
  dateInput: DateInputComponent | void;

  // Indicate control states:
  controlState: DetailsControlState = DetailsControlState.Default;
  isActive: boolean = true;
  // expand/collapse long values (used in textarea)
  isValueExpanded: boolean = false;

  private inputStatusChangeSubsr: Subscription;
  private selectStatusChangeSubsr: Subscription;
  private inputValueChangeSubsr: Subscription;
  private selectValueChangeSubsr: Subscription;

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get viewModeValue(): any {
    if (!_.isNil(this.staticValue)) { return this.staticValue; }
    const value = _.get(this.formGroup, `controls.${this.controlName}.value`);
    if (this.controlType === DetailsControlType.Select) {
      const selectedOption = _.find(this.options, (option) => {
        return _.isString(option.option) && _.isString(value)
          ? option.option.toLowerCase() === value.toLowerCase() : option.option === value;
      });
      if (this.isErrorSelect) {
        return value;
      }
      return _.get(selectedOption, 'title', this.viewValuePlaceholder);
    } else if (this.controlType === DetailsControlType.MultiSelect) {
      return _.join(value, ', ');
    } else if (this.controlType === DetailsControlType.Combined) {
      const selectValue = _.get((this.selectFormGroup || this.formGroup), `controls.${this.selectControlName}.value`);
      const selectedOption = _.find(this.options, (option) => {
        return _.isString(option.option) && _.isString(selectValue)
          ? option.option.toLowerCase() === selectValue.toLowerCase() : option.option === selectValue;
      });
      return `${value || this.viewValuePlaceholder} ${this.hideCombinedSelectDropdownValue ?
        '' : _.get(selectedOption, 'title', '')}`;
    } else if (this.controlType === DetailsControlType.Asset) {
      const thumbnail = _.get(this.formGroup, `value.thumbnail`);
      const mediumThumbnail = _.get(this.formGroup, `value.medium_thumbnail`);
      const downloadLink = _.get(this.formGroup, `value.download_link`);
      const uri = _.get(this.formGroup, `value.uri`);
      return thumbnail || mediumThumbnail || downloadLink || uri;
    } else {
      if (value) {
        return value;
      } else {
        return this.viewValuePlaceholder;
      }
    }
  }

  get controlStatus(): string {
    if (this.formGroup) {
      const control = this.formGroup.get(this.controlName);
      const selectControl = this.formGroup.get(this.selectControlName); // for combined
      if (!control && !selectControl) { return ''; }
      // if control is invalid always show red invalid state
      // if (!control.value && control.invalid) {
      //   return 'missing';
      // }
      if (control.invalid || _.get(selectControl, 'invalid', false)) {
        return 'invalid';
      } else {
        return 'valid';
      }
    } else {
      return '';
    }
  }

  /*
    * Used by letters counter
  */
  get inputValueLength(): number {
    return _.get(this.formGroup, `controls.${this.controlName}.value.length`, 0);
  }

  get isMasked(): boolean {
    return this.controlState === DetailsControlState.Masked;
  }

  get isFocused(): boolean {
    return this.controlState === DetailsControlState.Focused;
  }

  get isHighlighted(): boolean {
    return this.controlState === DetailsControlState.Highlighted;
  }

  get nativeElement(): any {
    return this.elementRef.nativeElement;
  }

  get expandTooltipHTML(): string {
    return `
      <div class='scorecard-tooltip-text'>
        <strong class='scorecard-tooltip-status'>
          ${this.isValueExpanded ? 'Expanded' : 'Collapsed'}
        </strong>
        <span class='scorecard-tooltip-note'>
          ${this.isValueExpanded ? 'Click collapse' : 'Click expand'}
        </span>
      </div>
    `;
  }

  get isTextarea(): boolean {
    return this.controlType === DetailsControlType.Textarea;
  }

  get controlPlaceholder(): string {
    return _.isNil(this.placeholder) ? this.defaultPlaceholder : this.placeholder;
  }

  get textMaskObj() {
    if (this.textMask) {
      return this.textMask;
    }
    return null;
  }

  get hasPrefixIcon(): boolean {
    return !_.isNil(this.prefixIcon);
  }

  get isAsset(): boolean {
    return this.controlType === DetailsControlType.Asset;
  }

  get assetFileName(): string {
    return _.get(this.formGroup, `value.filename`);
  }

  get isAssetInvalid(): boolean {
    const isInvalid = !this.viewModeValue || this.controlStatus === 'invalid' || this.forceInvalid;
    return isInvalid;
  }

  get isTemplate(): boolean {
    return this.controlType === DetailsControlType.Template;
  }

  get isErrorSelect(): boolean {
    if (this.controlType !== DetailsControlType.Select) {
      return false;
    }
    if (this.controlName === 'coreId') { // Core ID is system-generated and not used, as it is essentially a core class.
      return false;
    }
    const value = _.get(this.formGroup, `controls.${this.controlName}.value`);
    const selectedOption = _.find(this.options, (option) => {
      return _.isString(option.option) && _.isString(value)
        ? option.option.toLowerCase() === value.toLowerCase() : option.option === value;
    });
    return selectedOption === undefined && value !== undefined;
  }

  constructor(
    private ref: ChangeDetectorRef,
    private elementRef: ElementRef,
    private ngZone: NgZone,
  ) {}

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnChanges(changes: SimpleChanges) {
    if (changes.formGroup || changes.selectFormGroup ||
        changes.controlName || changes.selectControlName) {
      this.subscribeStatusChanges();
      this.subscribeValueChanges();
      this.ref.markForCheck();
    }
  }

  ngOnInit() {
    this.subscribeStatusChanges();
    if (this.controlType === DetailsControlType.Input) {
      this.subscribeControlClick();
    }
  }

  ngOnDestroy() {
    this.isActive = false;
    if (this.inputStatusChangeSubsr) { this.inputStatusChangeSubsr.unsubscribe(); }
    if (this.selectStatusChangeSubsr) { this.selectStatusChangeSubsr.unsubscribe(); }
    if (this.inputValueChangeSubsr) { this.inputValueChangeSubsr.unsubscribe(); }
    if (this.selectValueChangeSubsr) { this.selectValueChangeSubsr.unsubscribe(); }
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onInputFocus(controlName: string) {
    this.controlState = DetailsControlState.Focused;
    this.controlFocused.emit(controlName);
    this.ref.markForCheck();
  }

  onInputBlur() {
    this.controlState = DetailsControlState.Default;
    this.controlBlured.emit();
    this.ref.markForCheck();
  }

  /*
    * Handle select, date toogling
  */
  onControlToggle(isShown: boolean, controlName: string) {
    if (isShown) {
      this.controlState = DetailsControlState.Focused;
      this.controlFocused.emit(controlName);
    } else {
      this.controlState = DetailsControlState.Default;
      this.controlBlured.emit(controlName);
    }
    this.ref.markForCheck();
  }

  /*
    * Expand/collapse long value
    * Used in textarea
   */
  onLabelClick() {
    this.isValueExpanded = !this.isValueExpanded;
    this.ref.markForCheck();
  }

  /*******************************************
  * COMPONENT PUBLIC API
  *******************************************/

  public focusControl(controlName?: string): void {
    if (!this.editMode || this.isMasked) { return; }
    if (this.controlType === DetailsControlType.Input) {
      if (this.controlElement) {
        this.controlElement.nativeElement.focus();
      }
    } else if (this.controlType === DetailsControlType.Select) {
      if (this.selectDropdown) {
        this.selectDropdown.toggleDropdown();
      }
    } else if (this.controlType === DetailsControlType.Date) {
      _.defer(() => this.dateInput && this.dateInput.toggleDatepicker(true));
    } else if (this.controlType === DetailsControlType.Textarea) {
      if (this.controlElement) {
        this.controlElement.nativeElement.focus();
      }
    } else if (this.controlType === DetailsControlType.Combined) {
      if (controlName === this.controlName || controlName === this.fieldMap) {
        if (this.controlElement) {
          this.controlElement.nativeElement.focus();
        }
      } else {
        if (this.selectDropdown) {
          this.selectDropdown.toggleDropdown();
        }
      }
    }
    this.ref.markForCheck();
  }

  public maskControl(): void {
    this.controlState = DetailsControlState.Masked;
    this.ref.markForCheck();
  }

  public unmaskControl(): void {
    if (this.isMasked) {
      this.controlState = DetailsControlState.Default;
      this.ref.markForCheck();
    }
  }

  public highlightControl(): void {
    // prevent highlight masked and focused controls
    if (!this.isFocused && !this.isMasked) {
      this.controlState = DetailsControlState.Highlighted;
      this.ref.markForCheck();
    }
  }

  public unHighlightControl(): void {
    // prevent highlight masked control
    if (this.isHighlighted && !this.isMasked) {
      this.controlState = DetailsControlState.Default;
      this.ref.markForCheck();
    }
  }

  private subscribeStatusChanges(): void {
    if (_.get(this.formGroup, `controls.${this.controlName}`)) {
      if (this.inputStatusChangeSubsr) { this.inputStatusChangeSubsr.unsubscribe(); }
      this.inputStatusChangeSubsr = this.formGroup.get(this.controlName).statusChanges
      .pipe(distinctUntilChanged())
      .subscribe((status: string) => {
        this.ref.markForCheck();
        this.controlStatusChange.emit(status); // VALID/INVALID
      });
    }
    if (_.get((this.selectFormGroup || this.formGroup), `controls.${this.selectControlName}`)) {
      if (this.selectStatusChangeSubsr) { this.selectStatusChangeSubsr.unsubscribe(); }
      this.selectStatusChangeSubsr = (this.selectFormGroup || this.formGroup)
      .get(this.selectControlName)?.statusChanges
      .pipe(distinctUntilChanged())
      .subscribe((status: string) => {
        this.selectStatusChange.emit(status); // VALID/INVALID
      });
    }
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private subscribeValueChanges(): void {
    if (_.get(this.formGroup, `controls.${this.controlName}`)) {
      if (this.inputValueChangeSubsr) { this.inputValueChangeSubsr.unsubscribe(); }
      this.inputValueChangeSubsr = this.formGroup.get(this.controlName).valueChanges
      .pipe(
          distinctUntilChanged(),
        debounceTime(500)
      )
      .subscribe((value: string|number) => {
        this.ref.markForCheck();
        this.controlValueChanged.emit(value);
        this.controlPopulated.emit(!_.isNil(value) && String(value).length ? 1 : 0);
      });
    }
    if (_.get((this.selectFormGroup || this.formGroup), `controls.${this.selectControlName}`)) {
      if (this.selectValueChangeSubsr) { this.selectValueChangeSubsr.unsubscribe(); }
      this.selectValueChangeSubsr = (this.selectFormGroup || this.formGroup)
      .get(this.selectControlName)?.statusChanges
      .pipe(
          distinctUntilChanged(),
        debounceTime(500)
      )
      .subscribe((value: string|number) => {
        this.selectPopulated.emit(String(value).length ? 1 : 0);
      });
    }
  }

  private subscribeControlClick(): void {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(this.elementRef.nativeElement, 'click')
      .pipe(
          takeWhile(() => this.isActive),
        debounceTime(200)
      )
      .subscribe((event: MouseEvent) => {
        if (!this.editMode || this.hideLabel || this.readonly || this.isMasked || this.isFocused) {
          return;
        }
        this.focusControl(this.controlName);
      });
    });
  }

  public openAsset(event: MouseEvent): void {
    const downloadLink = _.get(this.formGroup, `value.download_link`);
    const uri = _.get(this.formGroup, `value.uri`);
    if (downloadLink || uri) {
      window.open(downloadLink || uri, '_blank');
    }
    event.stopPropagation();
  }
}
